<script setup lang="ts">
import type { Schemas } from '#shopware'

const props = defineProps<{
    content: Schemas['CmsElementWebflowSlider']
}>()

const articles = computed(() => {
    const raw = props.content?.data?.articles
    return raw ? Object.values(raw) : []
})

const title = computed(() => props.content?.config?.title?.value)
const subTitle = computed(() => props.content?.config?.subTitle?.value)
const learnMoreButtonText = computed(() => props.content?.config?.learnMoreButtonText?.value)
</script>

<template>
    <div class="flex flex-col mb-16">
        <div
            v-if="title"
            class="text-4xl font-fjalla-one font-normal uppercase mb-1 flex flex-col md:flex-row gap-4 items-start md:items-center"
        >
            <MiscIcon
                name="greenfeed_logo"
                class="h-[48px] w-[162px] text-primary md:-mt-[13px]"
            />
            <span>{{ title }}</span>
        </div>
        <div v-if="subTitle">
            {{ subTitle }}
        </div>
    </div>

    <SliderBase
        v-if="articles.length > 0"
        :slides-per-view="{
            xs: 1.3,
            sm: 2.3,
            md: 3,
            lg: 5
        }"
        :slide-count="articles.length"
        :mobile-grid="false"
        :display-nav="true"
        :space-between="24"
        progress-config="disabled"
        :aria-label="$t('webflowSlider.main')"
        class="mb-8"
    >
        <template #default="{ index }">
            <div
                v-if="articles[index] != null"
                class="text-base-content flex flex-col gap-4 webflow-swiper-slide w-full h-full"
            >
                <MiscLink
                    :to="articles[index]!.data?.url ?? ''"
                    class="flex flex-col gap-4"
                    target="_blank"
                >
                    <div class="relative">
                        <div class="after:content-[' '] after:absolute after:bottom-0 after:left-0 after:h-4/5 md:after:h-full after:w-full after:bg-[linear-gradient(180deg,rgba(0,0,0,0.00)0%,rgba(0,0,0,0.65)100%)] hero-slider-image-container h-full w-full">
                            <img
                                v-if="articles[index]!.label !== 'Live Golf'"
                                class="w-full h-full aspect-square object-cover cursor-pointer"
                                loading="lazy"
                                :src="articles[index]!.data?.image"
                                :alt="articles[index]!.label"
                            >
                            <img
                                v-else
                                class="w-full h-full aspect-square object-cover cursor-pointer"
                                loading="lazy"
                                :src="articles[index]!.data?.backgroundImage"
                                alt="Greenfeed Event Placeholder"
                            >
                        </div>
                        <div
                            v-if="articles[index]!.label !== 'Live Golf'"
                            class="font-fjalla-one font-normal uppercase text-5xl text-base-100 absolute w-full inset-0 flex justify-center items-center"
                        >
                            {{ articles[index]!.label }}
                        </div>
                        <img
                            v-else
                            class="absolute inset-0 w-full flex justify-center items-center p-24 md:p-16"
                            loading="lazy"
                            :src="articles[index]!.data?.flag"
                            alt="Flag"
                        >
                    </div>
                    <div
                        v-if="articles[index]!.data?.title"
                        class="text-lg font-fjalla-one font-normal uppercase"
                    >
                        {{ articles[index]!.data.title }}
                    </div>
                    <div
                        v-if="articles[index]!.data?.description"
                        class="text-xs"
                    >
                        {{ articles[index]!.data.description }}
                    </div>
                    <div
                        v-if="articles[index]!.label === 'Live Golf'"
                        class="text-xs"
                    >
                        {{ $t('webflowSlider.liveGolfDescription') }}
                    </div>
                </MiscLink>
            </div>
        </template>
    </SliderBase>

    <MiscLink
        v-if="learnMoreButtonText"
        to="https://www.greenfeed.news/"
        target="_blank"
        class="btn btn-primary self-start"
    >
        {{ learnMoreButtonText }}
        <MiscIcon name="arrow-right" />
    </MiscLink>
</template>
